/**
 * src/AppShell.jsx — Main Application Shell
 * AC Nautitech
 *
 * Routes to views based on active tab.
 * Each view is wrapped in SafeView (ErrorBoundary + Suspense)
 * so a crash in one view never kills the whole app.
 */

import { useState, Suspense, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './auth';
import { logRouteChange, setTraceContext } from './lib/observability';
import { logAppError } from './lib/errorReporter';
import { readAndClearBreadcrumb } from './lib/chunkReloadBreadcrumb';
import { resolveRestoreTab } from './lib/resolveRestoreTab';
import NavigationShell from './components/NavigationShell';
import BackToAppButton from './components/shared/BackToAppButton';
import ErrorBoundary from './components/ErrorBoundary';
import lazyWithRetry from './lib/lazyWithRetry';
import { AppDataProvider } from './lib/AppDataContext';
import OfflineBanner from './components/shared/OfflineBanner';
import OfflineSyncIndicator from './components/shared/OfflineSyncIndicator';
import ProviderStatusBanner from './components/shared/ProviderStatusBanner';
import UpdateBanner from './components/shared/UpdateBanner';
import VoiceQueueTray from './components/voice/VoiceQueueTray';
import UploadJobTrackerHost from './components/voice/UploadJobTrackerHost';
import { requestProjectOpen } from './lib/nav/projectNav';
import { subscribeQuoteOpen } from './lib/nav/quoteNav';
import { requestDealOpen } from './lib/nav/dealNav';
import { routeSearchOpen } from './lib/nav/searchOpenRouter';
import { parseDeepLink, dispatchDeepLink } from './lib/nav/parseDeepLink';
import { saveCurrentNav, applyReloadRestore } from './lib/nav/reloadRestore';
import { startWorker, stopWorker } from './lib/voice/queueWorker';
import { voiceProcessFn } from './lib/voice/voiceProcessFn';
import AgentShellTrigger from './components/agent/AgentShellTrigger';
import { uploadManager } from './lib/uploadManager.js';
// Resume-on-reopen for interrupted large resumable uploads (TICKET-MINIO-RESUMABLE
// Slice 3b). No-op unless VITE_MINIO_RESUMABLE is on — flag-gated inside.
import { resumeIncompleteUploadsOnStart } from './lib/api/resumeUploadsOnStart.js';
// An email queued behind the 30s Undo window when the tab died (TICKET-858).
import { recoverPendingEmails, setUserIdForPendingEmails } from './lib/undoSend';
import { start as startOfflineSync, stop as stopOfflineSync, subscribe as subscribeOfflineSync } from './lib/offline/syncEngine';
import { prepareOfflineCache, getLastPreparedAt } from './lib/offline/prepareOffline';
import { getOnline } from './lib/offline/networkStatus';
import { classifyDroppedOps } from './lib/offline/dropToast';
import { isOfflineTasksEnabled } from './lib/featureFlags';
import DebugPanelProviderBridge from './components/debug/DebugPanelProviderBridge.jsx';
import { shouldMountDebugPanel } from './lib/debug/debugGate.js';
import { useToast } from './components/shared/ToastProvider.jsx';
import { useT } from './lib/i18n/I18nContext';
import StuckLoadingWatch, { reportStuckReloadBreadcrumb } from './components/shared/StuckLoadingWatch';
import { autoEnablePushOnce, listenForPushToasts } from './lib/push/registerPush.js';
import { PendingReviewsProvider } from './lib/hooks/usePendingReviews';
import QuoteReviewToaster from './components/pipeline/quote/QuoteReviewToaster';

// Direct import for Focus (most used, no lazy load delay)
import FocusView from './views/FocusView';

// Lazy load other views — each gets its own chunk
const ProjectsView = lazyWithRetry(() => import('./views/ProjectsView'), 'projects');
const GanttView = lazyWithRetry(() => import('./views/GanttView'), 'gantt');
const PipelineView = lazyWithRetry(() => import('./views/PipelineView'), 'pipeline');
const TeamView = lazyWithRetry(() => import('./views/TeamView'), 'team');
const BillingView = lazyWithRetry(() => import('./views/BillingView'), 'billing');
const AnalyticsView = lazyWithRetry(() => import('./views/AnalyticsView'), 'analytics');
const SettingsView = lazyWithRetry(() => import('./views/SettingsView'), 'settings');
const DirectoryView = lazyWithRetry(() => import('./views/DirectoryView'), 'directory');
const ProcurementView = lazyWithRetry(() => import('./views/ProcurementView'), 'procurement');
const ReorderView = lazyWithRetry(() => import('./views/ReorderView'), 'reorder');
const AdminObservability = lazyWithRetry(() => import('./views/AdminObservability'), 'admin-observability');
const AdminTraces = lazyWithRetry(() => import('./views/AdminTraces'), 'admin-traces');
const AdminPerformance = lazyWithRetry(() => import('./views/AdminPerformance'), 'admin-performance');
const CommandCenterView = lazyWithRetry(() => import('./views/CommandCenterView'), 'command-center');
const MyDayView = lazyWithRetry(() => import('./views/MyDayView'), 'myday');
const WorkLogsView = lazyWithRetry(() => import('./views/WorkLogsView'), 'worklogs');
const CRMContactsView = lazyWithRetry(() => import('./views/CRMContactsView'), 'crm');
const AgentChat = lazyWithRetry(() => import('./views/AgentChat'), 'agent');
const OperationsView = lazyWithRetry(() => import('./views/OperationsView'), 'operations');
const KnowledgeDashboardView = lazyWithRetry(() => import('./views/KnowledgeDashboardView'), 'knowledge');
const InterpreterView = lazyWithRetry(() => import('./views/InterpreterView'), 'interpreter');
const WorkDayView = lazyWithRetry(() => import('./views/WorkDayView'), 'workday');
const MisDocumentosView = lazyWithRetry(() => import('./views/MisDocumentosView'), 'mis-documentos');
const BookkeepingReconcileView = lazyWithRetry(() => import('./views/BookkeepingReconcileView'), 'bookkeeping');
// Opened only via a `/quote/<id>` deep-link (approver's push tap) — lazy so it
// never weighs on the initial bundle.
const QuoteBuilderScreen = lazyWithRetry(() => import('./components/pipeline/quote/QuoteBuilderScreen'), 'quote-builder');

/* ── View Registry ─────────────────────────────────────────────────── */
const VIEW_REGISTRY = {
  // passNavigate: the view gets onNavigate(tab) to jump to another tab (shortcut cards).
  command:  { Component: CommandCenterView, label: 'Command Center', lazy: true, passRole: true, passNavigate: true },
  focus:    { Component: FocusView, label: 'Focus',    lazy: false },
  tasks:    { Component: GanttView, label: 'Planner',  lazy: true, passRole: true },
  timeline: { Component: GanttView, label: 'Timeline', lazy: true, passRole: true },
  projects: { Component: ProjectsView, label: 'Projects', lazy: true, passRole: true },
  pipeline: { Component: PipelineView, label: 'Pipeline', lazy: true },
  directory: { Component: DirectoryView, label: 'Directory', lazy: true },
  team:     { Component: TeamView,     label: 'Team',     lazy: true },
  billing:  { Component: BillingView,  label: 'Billing',  lazy: true, passRole: true },
  procurement: { Component: ProcurementView, label: 'Procurement', lazy: true },
  analytics: { Component: AnalyticsView, label: 'Analytics', lazy: true },
  myday:     { Component: MyDayView,     label: 'My Day',     lazy: true },
  worklogs:  { Component: WorkLogsView,  label: 'Work Logs',  lazy: true },
  crmcontacts: { Component: CRMContactsView, label: 'CRM Contacts', lazy: true },
  operations: { Component: OperationsView, label: 'Operations', lazy: true },
  agent:   { Component: AgentChat,  label: 'Agent',  lazy: true },
  knowledge: { Component: KnowledgeDashboardView, label: 'Knowledge', lazy: true },
  interpreter: { Component: InterpreterView, label: 'Interpreter', lazy: true },
  workday: { Component: WorkDayView, label: 'Work Day', lazy: true },
  settings: { Component: SettingsView, label: 'Settings', lazy: true },
  'mis-documentos': { Component: MisDocumentosView, label: 'Mis Documentos', lazy: true },
  bookkeeping: { Component: BookkeepingReconcileView, label: 'Bookkeeping', lazy: true },
};

/* ── Loading Spinner ───────────────────────────────────────────────── */
// TICKET-1104: a lazy view chunk that never arrives (dead WebKit network) gets the
// same banner + guarded hard reload as a stuck data load.
const ViewLoader = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48 }}>
    <StuckLoadingWatch view="view-chunk" force />
    <div style={{
      width: 32, height: 32, borderRadius: '50%',
      border: '3px solid var(--bdr)', borderTopColor: 'var(--navy)',
      animation: 'spin 800ms linear infinite',
    }} />
  </div>
);

/* ── SafeView: ErrorBoundary + Suspense per view ───────────────────── */
function SafeView({ name, children }) {
  // key={name} ensures boundary resets when switching tabs
  return (
    <ErrorBoundary key={name} viewName={name}>
      <Suspense fallback={<ViewLoader />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}

/* ── Main Shell ────────────────────────────────────────────────────── */
export default function AppShell() {
  const { user, role: authRole, profile, signOut } = useAuth();
  const { showToast, showError } = useToast();
  const { t } = useT();
  const [activeTab, _setActiveTabRaw] = useState('focus');
  // Phase A1 — capture nav trigger source so nav_trace can record whether the
  // route change came from a click, an event, voice, etc.
  const pendingTriggerRef = useRef('initial');
  const setActiveTab = useCallback((tab, trigger = 'click') => {
    pendingTriggerRef.current = trigger;
    _setActiveTabRaw(tab);
  }, []);

  // A `/quote/<id>` deep-link (an approver tapping the "needs your approval" push)
  // opens the full-screen quote builder over whatever view is active — the builder
  // is self-contained (fetches its own quote), so it needs no tab switch. Set by
  // dispatchDeepLink's requestQuoteOpen; rendered as a global overlay below.
  const [deepLinkQuoteId, setDeepLinkQuoteId] = useState(null);

  // A click on a quote-review task's "Open quote" affordance anywhere in the
  // tree (TaskCard/TaskDetailPanel) requests this overlay via quoteNav —
  // same race-free pattern as requestProjectOpen.
  useEffect(() => subscribeQuoteOpen(setDeepLinkQuoteId), []);

  // _roleOverrideTick forces re-render when localStorage override changes
  const [_roleOverrideTick, setRoleOverrideTick] = useState(0);
  useEffect(() => {
    const handler = () => setRoleOverrideTick(t => t + 1);
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // CEO can override role for UI testing via Settings > Role Switcher.
  // Override is stored in localStorage as 'acn_role_override'.
  // Backend always uses the real JWT role — this only affects frontend rendering.
  const storedOverride = typeof window !== 'undefined'
    ? localStorage.getItem('acn_role_override')
    : null;
  const effectiveRole = (authRole === 'ceo' && storedOverride) ? storedOverride : (authRole || 'ceo');

  // Offline-tasks sync engine (flag-gated). Drains the offline outbox to Supabase
  // when connectivity returns. Singleton — start once per session when enabled.
  const offlineSyncEnabled = isOfflineTasksEnabled(profile);

  // Phase Z — DebugPanel toggle state
  const [debugOpen, setDebugOpen] = useState(false);
  const debugFlagEnabled = import.meta.env?.VITE_ENABLE_DEBUG_PANEL === 'true';
  const debugMountable = shouldMountDebugPanel(effectiveRole, debugFlagEnabled);

  useEffect(() => {
    uploadManager.start();
    // Drain any interrupted resumable uploads from a prior session (flag-gated no-op
    // when VITE_MINIO_RESUMABLE is off). Fire-and-forget — never blocks app start.
    resumeIncompleteUploadsOnStart().catch((err) => console.warn('[AppShell] resume-on-start failed:', err?.message));
    return () => uploadManager.stop();
  }, []);

  // TICKET-964: set the user id for scoping pending email keys so they don't leak
  // across users on a shared browser. Runs once per user login.
  useEffect(() => {
    if (user?.id) setUserIdForPendingEmails(user.id);
  }, [user?.id]);

  // TICKET-858 (CEO 2026-09-21: "save temporary the email, so in case it crashes,
  // you can recall the email"). An email inside its 30s Undo window lives in a
  // timer; a crashed tab takes the timer with it. The snapshot outlives the
  // session, and this hands the words back as a composer draft.
  //
  // Recovered as a DRAFT and never auto-sent: the crash may have happened AFTER
  // the request left, so resending could duplicate a mail the client already has.
  // The toast says that out loud rather than implying the email is safely unsent —
  // a recovery the user misreads is its own defect.
  useEffect(() => {
    if (!user?.id) return;
    setUserIdForPendingEmails(user.id); // set before reading: effect order is not a contract
    const recovered = recoverPendingEmails();
    if (!recovered.length) return;
    showToast({
      severity: 'warning',
      message: t('emailSend.recovered', { count: recovered.length, to: recovered[0].to }),
      durationMs: 12000,
    });
  }, [user?.id, showToast, t]);

  useEffect(() => {
    if (!offlineSyncEnabled) return undefined;
    startOfflineSync();
    return () => stopOfflineSync();
  }, [offlineSyncEnabled]);

  // TICKET-654: a photo capture that fails to upload after MAX_RETRIES was
  // silently dropped — only a spine log row, nothing the CEO ever saw in the
  // app. syncNow() runs on every capture (PhotoCapture.jsx) independent of the
  // offlineSyncEnabled gate above, so this subscription must too.
  useEffect(() => {
    return subscribeOfflineSync((evt) => {
      const dropped = evt?.lastResult?.droppedOps;
      if (!Array.isArray(dropped) || !dropped.length) return;
      // TICKET-1036: the photos-specific copy names the concrete recovery
      // (retake/re-add); every other domain used to drop silently with no
      // toast at all — surface those too, with a generic message.
      const { photos, other } = classifyDroppedOps(dropped);
      if (photos) showError(t('photos.uploadDropped'), { durationMs: 0 });
      if (other) showError(t('offline.uploadDropped'), { durationMs: 0 });
    });
  }, [showError, t]);

  // Auto-warm the offline cache on startup (CEO request 2026-06-12). The shared
  // domains already cache via their hooks' initial load; this background pass
  // ALSO covers Analytics (whose finance data otherwise only loads when you open
  // that tab) and refreshes everything, so every text-based tab is ready for a
  // flight without opening each one or tapping Download. Heavy media (images)
  // stay lazy/online by design. Deferred 4s so it never competes with first
  // paint, throttled to once per 10 min, online + flag gated.
  useEffect(() => {
    if (!offlineSyncEnabled || !getOnline()) return undefined;
    const last = getLastPreparedAt();
    if (last && Date.now() - last < 10 * 60 * 1000) return undefined;
    const id = setTimeout(() => { prepareOfflineCache().catch(() => {}); }, 4000);
    return () => clearTimeout(id);
  }, [offlineSyncEnabled]);

  useEffect(() => {
    // V-2/V-4: drain the voice session queue. startWorker is a no-op in
    // jsdom (navigator.locks missing) and idempotent if called twice.
    startWorker({ processFn: voiceProcessFn }).catch((err) => {
      console.warn('[AppShell] voice queue worker start failed:', err?.message);
    });
    // TICKET-CODEX-3172673720: surface Web Locks unavailability as a console
    // warning so developers and support can diagnose the issue. The worker
    // dispatches this event when navigator.locks is missing (non-secure context
    // or legacy browser); the user's queued recordings stay safe in IndexedDB
    // but will not upload until they open a supported browser.
    function onVoiceQueueUnavailable(e) {
      console.warn('[AppShell] Voice queue unavailable:', e.detail?.reason,
        '— voice recordings will not upload in this browser/context.');
    }
    window.addEventListener('acn:voice-queue-unavailable', onVoiceQueueUnavailable);
    return () => {
      stopWorker();
      window.removeEventListener('acn:voice-queue-unavailable', onVoiceQueueUnavailable);
    };
  }, []);

  // Web push laptop parity (CEO 2026-08-04): mobile got push because someone
  // opened Settings and hit "Enable" there; laptops never did. Ask once per
  // device automatically, and bridge SW push events into an in-app toast so a
  // focused desktop tab surfaces the notification too, not just the OS one.
  useEffect(() => {
    autoEnablePushOnce();
    return listenForPushToasts((payload) => {
      const url = payload?.data?.url;
      showToast({
        severity: 'info',
        message: [payload?.title, payload?.body].filter(Boolean).join(' — ') || 'AC Nautitech',
        durationMs: 8000,
        action: url ? {
          label: 'View',
          onClick: () => dispatchDeepLink(parseDeepLink(url), {
            setActiveTab: (tab) => setActiveTab(tab, 'push:toast'),
            requestDealOpen,
            requestProjectOpen,
            requestQuoteOpen: setDeepLinkQuoteId,
            isKnownTab: (tab) => Boolean(VIEW_REGISTRY[tab]),
          }),
        } : null,
      });
    });
  }, [setActiveTab, showToast]);

  // 2026-04-19 — boot-time path → tab mapping. Previously /agent rendered
  // FocusView because the SPA never read the URL; any deep link was stuck on
  // the default tab until the user clicked a nav item. Now we map known
  // pathnames to tabs on first mount. Reorder + admin observability still
  // short-circuit earlier in this component.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // TICKET-209 — entity deep-links open that boat directly: a `/deal/<id>` or
    // `/project/<id>` link WhatsApp'd to a teammate switches to the owning tab
    // AND sets a persistent open-request (dealNav/projectNav) that survives the
    // cold-boot race where the link lands before the view mounts. Bare `/<tab>`
    // links keep the prior behaviour. Parsing + dispatch are unit-tested in
    // parseDeepLink so this stays thin glue.
    dispatchDeepLink(parseDeepLink(window.location.pathname), {
      setActiveTab: (tab) => setActiveTab(tab, 'url:initial'),
      requestDealOpen,
      requestProjectOpen,
      requestQuoteOpen: setDeepLinkQuoteId,
      isKnownTab: (tab) => Boolean(VIEW_REGISTRY[tab]),
    });
  }, [setActiveTab]);

  // TICKET-019 Phase 2 — chunk-reload view restore.
  //
  // When ErrorBoundary auto-reloads on a stale chunk, it writes a
  // sessionStorage breadcrumb with `from_view`. On the next boot, read it,
  // log a `chunk_reload_completed` event with `time_to_recover_ms`, and
  // restore the user's prior tab so they don't land on Focus default.
  // URL deep-link wins (handled by the effect above); this only kicks in
  // when the in-memory tab state was the only signal of where the user was.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const breadcrumb = readAndClearBreadcrumb();
    if (!breadcrumb || !breadcrumb.from_view) return;
    try {
      logAppError({
        type: 'chunk_reload_completed',
        message: `Chunk reload recovered from ${breadcrumb.from_view}`,
        view: breadcrumb.from_view,
        metadata: {
          from_view: breadcrumb.from_view,
          time_to_recover_ms: breadcrumb.time_to_recover_ms,
          error_msg: breadcrumb.error_msg,
        },
      });
    } catch { /* observability is best-effort; never break boot */ }
    const restoreTab = resolveRestoreTab(breadcrumb, VIEW_REGISTRY);
    if (restoreTab) {
      setActiveTab(restoreTab, 'event:chunk-reload-restore');
    }
  }, [setActiveTab]);

  // UPDATE-banner reload restore (CEO 2026-06-18): re-open the tab + project the
  // user was on so a "new version" reload doesn't dump them on the default Focus
  // view. Reuses the race-free requestProjectOpen deep-link. Runs once on boot,
  // BEFORE the persist effect below overwrites the saved nav.
  useEffect(() => {
    applyReloadRestore({ setActiveTab, requestProjectOpen, requestDealOpen });
  }, [setActiveTab]);

  // TICKET-1104: report a hard reload the previous (network-dead) page forced.
  useEffect(() => { reportStuckReloadBreadcrumb(); }, []);

  // Cross-view navigation: switch to agent tab when open-agent-chat fires
  useEffect(() => {
    const handler = () => setActiveTab('agent', 'event:open-agent-chat');
    window.addEventListener('open-agent-chat', handler);
    return () => window.removeEventListener('open-agent-chat', handler);
  }, [setActiveTab]);

  // Cross-view navigation: SmartSearchOverlay dispatches acn:search-open with
  // { type: 'task'|'project'|'bundle'|'person'|'contact'|'deal', id } when a
  // result is clicked. Routing (incl. the race-free projectNav/dealNav deep-link
  // requests so the target view opens the detail even when it wasn't mounted at
  // dispatch time) lives in searchOpenRouter so it stays unit-testable.
  useEffect(() => {
    const handler = (e) => routeSearchOpen(e.detail, setActiveTab);
    window.addEventListener('acn:search-open', handler);
    return () => window.removeEventListener('acn:search-open', handler);
  }, [setActiveTab]);

  // ── Floating agent bubble context ──
  const [contextProjectId, setContextProjectId] = useState(null);
  const [contextDealId, setContextDealId] = useState(null);
  const [contextProjectName, setContextProjectName] = useState(null);
  const [contextDealName, setContextDealName] = useState(null);

  // Listen for context updates from views
  useEffect(() => {
    const handler = (e) => {
      const d = e.detail || {};
      setContextProjectId(d.projectId || null);
      setContextDealId(d.dealId || null);
      setContextProjectName(d.projectName || null);
      setContextDealName(d.dealName || null);
    };
    window.addEventListener('view-context-change', handler);
    return () => window.removeEventListener('view-context-change', handler);
  }, []);

  // Clear context on tab change
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('view-context-change', { detail: {} }));
  }, [activeTab]);

  // Continuously remember the user's spot (tab + open project) so an UPDATE-banner
  // reload can restore it (consumed by applyReloadRestore on the next boot).
  useEffect(() => {
    saveCurrentNav({ tab: activeTab, projectId: contextProjectId, dealId: contextDealId });
  }, [activeTab, contextProjectId, contextDealId]);

  // Phase A1 — keep ambient trace context in sync so every _log() merges in
  // activeTab, userRole, and current floating-bubble entity context.
  useEffect(() => {
    setTraceContext({
      activeTab,
      userRole: effectiveRole || null,
      contextProjectId: contextProjectId || null,
      contextDealId: contextDealId || null,
    });
  }, [activeTab, effectiveRole, contextProjectId, contextDealId]);

  // Phase A1 — emit nav_trace lifecycle for every view switch.
  const prevViewRef = useRef(null);
  useEffect(() => {
    if (prevViewRef.current === activeTab) return;
    const trigger = pendingTriggerRef.current || 'unknown';
    pendingTriggerRef.current = null;
    const handle = logRouteChange(prevViewRef.current || '_initial', activeTab, {
      userRole: effectiveRole || null,
      trigger,
    });
    prevViewRef.current = activeTab;
    const raf = requestAnimationFrame(() => handle.complete());
    return () => cancelAnimationFrame(raf);
  }, [activeTab, effectiveRole]);

  // Phase Z — Ctrl+Shift+D keyboard toggle for DebugPanel (only arms when mountable)
  useEffect(() => {
    if (!debugMountable) return;
    const handler = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
        e.preventDefault();
        setDebugOpen(v => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [debugMountable]);

  const renderView = useCallback(() => {
    const entry = VIEW_REGISTRY[activeTab];
    if (!entry) {
      return (
        <div style={{ padding: 24, color: 'var(--tm)' }}>View not found</div>
      );
    }

    const { Component, label, passRole, passNavigate } = entry;
    const props = passRole ? { role: effectiveRole } : {};
    if (passNavigate) props.onNavigate = (tab) => setActiveTab(tab, `view:${activeTab}`);

    return (
      <SafeView name={label}>
        <Component {...props} />
      </SafeView>
    );
  }, [activeTab, effectiveRole, setActiveTab]);

  // Handle /reorder/{id} URL from QR scans — full-screen, no sidebar
  const reorderMatch = window.location.pathname.match(/^\/reorder\/(.+)$/);
  if (reorderMatch) {
    return (
      <AppDataProvider>
        <BackToAppButton />
        <Suspense fallback={<ViewLoader />}>
          <ReorderView materialId={reorderMatch[1]} />
        </Suspense>
      </AppDataProvider>
    );
  }

  // Handle /admin/observability — CEO/admin voice A/B dashboard (full-screen)
  if (window.location.pathname === '/admin/observability') {
    return (
      <AppDataProvider>
        <BackToAppButton />
        <Suspense fallback={<ViewLoader />}>
          <AdminObservability />
        </Suspense>
      </AppDataProvider>
    );
  }

  // Handle /admin/traces — observability spine viewer (PAUL Plan 92-01).
  // Distinct from /admin/observability (voice A/B dashboard) — different
  // feature, different data source. RLS on app_error_logs enforces ceo+admin.
  if (window.location.pathname === '/admin/traces') {
    return (
      <AppDataProvider>
        <BackToAppButton />
        <Suspense fallback={<ViewLoader />}>
          <AdminTraces />
        </Suspense>
      </AppDataProvider>
    );
  }

  // Handle /admin/performance — per-area p95 tiles (TICKET-1263). CEO + admin;
  // the server gate on /api/ops perf.summary is the real one.
  if (window.location.pathname === '/admin/performance') {
    return (
      <AppDataProvider>
        <BackToAppButton />
        <Suspense fallback={<ViewLoader />}>
          <AdminPerformance />
        </Suspense>
      </AppDataProvider>
    );
  }

  return (
    <AppDataProvider>
      <OfflineBanner />
      <OfflineSyncIndicator />
      <ProviderStatusBanner />
      <UpdateBanner />
      <VoiceQueueTray />
      {/* Single voice-status surface = the top VoiceQueueTray (CEO 2026-06-21:
          consolidated the duplicate bottom-right "Processing recording" card into
          the tray — one indicator, minimizable). This invisible host keeps
          upload-job phases advancing tab-independently (tracker rehomed here). */}
      <UploadJobTrackerHost />
      {/* TICKET-1104: any tab stuck on the app-wide load -> banner, then one guarded hard reload. */}
      <StuckLoadingWatch view={activeTab} />
      {/* TICKET-1098: one pending-review list for nav badge, Pipeline, Tasks + app-open toast. */}
      <PendingReviewsProvider role={effectiveRole} refreshKey={deepLinkQuoteId}>
        <QuoteReviewToaster onShowAll={() => setActiveTab('pipeline', 'toast:quote-review')} />
        <NavigationShell role={effectiveRole} realRole={authRole} activeTab={activeTab} onTabChange={setActiveTab} onSignOut={signOut}>
          {renderView()}
        </NavigationShell>
      </PendingReviewsProvider>
      {/* Command Center embeds the same assistant as a pane — no second launcher there. */}
      {activeTab !== 'agent' && activeTab !== 'command' && (
        <AgentShellTrigger activeView={activeTab} />
      )}
      {/* `/quote/<id>` deep-link overlay — an approver tapping the "needs your
          approval" push lands straight on the quote's Approve screen. Self-contained
          full-screen portal; Suspense while its chunk loads. */}
      {deepLinkQuoteId && (
        <Suspense fallback={null}>
          <QuoteBuilderScreen quoteId={deepLinkQuoteId} onClose={() => setDeepLinkQuoteId(null)} />
        </Suspense>
      )}
      {/* Phase Z — DebugPanel: Ctrl+Shift+D toggle, CEO + flag-gated. Bridge reads store from AppDataContext. */}
      {debugMountable && (
        <DebugPanelProviderBridge
          role={effectiveRole}
          flagEnabled={debugFlagEnabled}
          open={debugOpen}
          onClose={() => setDebugOpen(false)}
          userId={null}
        />
      )}
    </AppDataProvider>
  );
}
